import { createAction, createSelector } from "@reduxjs/toolkit";
import { addHighlightLayers, changeSearch, removeHighlightLayers, selectLayer, setLayersConfigDisplay, setLayersDisplay, } from "./layersReducer.js";
import { addSourceMetadata, addSourceTileJSON, selectSource, selectSourceLayer, selectSourceLayerLayer, } from "./sourcesReducer.js";
import { setMapkaApiKey } from "./apiKeyReducer.js";
import { assistantChange, changeChatId, toggleChat, toggleHistory } from "./chatReducer.js";
import { selectMarker } from "./markerReducer.js";
import { get } from "es-toolkit/compat";
import { withRandomId } from "../utils/crypto.js";
export const setPrimaryVersion = createAction("history/setPrimaryVersion");
export const setSecondaryVersion = createAction("history/setSecondaryVersion");
export const redoAction = createAction("history/redo");
export const undoAction = createAction("history/undo");
export const restoreVersion = createAction("history/restoreVersion");
export const initStyle = createAction("history/initStyle");
function getSettingsState(spec) {
    const settings = {};
    if ("center" in spec)
        settings.center = spec.center;
    if ("zoom" in spec)
        settings.zoom = spec.zoom;
    if ("bearing" in spec)
        settings.bearing = spec.bearing;
    if ("pitch" in spec)
        settings.pitch = spec.pitch;
    if ("light" in spec)
        settings.light = spec.light;
    if ("sky" in spec)
        settings.sky = spec.sky;
    if ("projection" in spec)
        settings.projection = spec.projection;
    return settings;
}
function entryToState(present, entry) {
    const { spec, messageId, text } = entry;
    const defaultGroups = spec.layers.map(layer => ({
        value: layer.id,
    }));
    const groups = get(spec, "metadata.mapka.layerGroups", defaultGroups);
    const markers = get(spec, "metadata.mapka.markers", []);
    return {
        settings: getSettingsState(spec),
        layers: {
            ...present.layers,
            selected: null,
            listDisplay: "grouped",
            configDisplay: "style",
            layers: spec.layers,
            highlightLayers: [],
            search: "",
            groups,
        },
        sources: {
            ...present.sources,
            selectedSource: null,
            sources: spec.sources,
            configDisplay: "code",
        },
        chat: present.chat,
        markers: {
            markers: markers.map(withRandomId),
            selectedMarker: null,
        },
        apiKey: present.apiKey,
        init: {
            ...present.init,
            published: false,
            current: false,
            messageId,
            text,
        },
    };
}
function initState(state, style, versions, accountName) {
    const { present } = state;
    if (!style || !versions) {
        return state;
    }
    const { currentVersionId, publishedVersionId, } = style;
    const sortedVersions = versions.toSorted((a, b) => b.id - a.id);
    const current = sortedVersions.find(v => v.id === currentVersionId);
    const restOfVersions = sortedVersions.filter(v => v !== current);
    if (!current)
        throw new Error("Current version not found");
    const versionToState = (version) => {
        const { spec } = version;
        const defaultGroups = spec.layers.map(layer => ({
            value: layer.id,
        }));
        const groups = get(spec, "metadata.mapka.layerGroups", defaultGroups);
        const markers = get(spec, "metadata.mapka.markers", []);
        return {
            settings: getSettingsState(spec),
            layers: {
                ...present.layers,
                selected: null,
                listDisplay: "grouped",
                configDisplay: "style",
                layers: spec.layers,
                highlightLayers: [],
                search: "",
                groups,
            },
            sources: {
                ...present.sources,
                selectedSource: null,
                sources: spec.sources,
                configDisplay: "code",
            },
            chat: {
                ...present.chat,
                chatId: 0,
            },
            markers: {
                markers: markers.map(withRandomId),
                selectedMarker: null,
            },
            apiKey: present.apiKey,
            init: {
                spec,
                style,
                version,
                accountName,
                published: publishedVersionId === version.id,
                current: currentVersionId === version.id,
            },
        };
    };
    return {
        past: restOfVersions.map(versionToState),
        present: versionToState(current),
        future: [],
        primaryVersion: null,
        secondaryVersion: null,
    };
}
export function historyEnhancer(reducer) {
    const initialState = {
        past: [],
        present: reducer(undefined, { type: "INIT" }),
        future: [],
        primaryVersion: null,
        secondaryVersion: null,
    };
    let savedIntState = undefined;
    return (state = initialState, action) => {
        const { past, present, future } = state;
        switch (action.type) {
            case initStyle.type: {
                const { payload: { account, style, versions, }, } = action;
                const initializedState = initState(state, style, versions, account.name);
                savedIntState = initializedState;
                return initializedState;
            }
            case undoAction.type: {
                if (!past.length) {
                    return state;
                }
                const previous = past[past.length - 1];
                const newPast = past.slice(0, past.length - 1);
                return {
                    past: newPast,
                    present: {
                        ...previous,
                        sources: {
                            ...previous.sources,
                            tilejson: present.sources.tilejson,
                            metadata: present.sources.metadata,
                        },
                        chat: present.chat,
                        apiKey: present.apiKey,
                        init: present.init,
                    },
                    future: [present, ...future],
                    primaryVersion: null,
                    secondaryVersion: null,
                };
            }
            case redoAction.type: {
                if (!future.length) {
                    return state;
                }
                const [next] = future;
                const newFuture = future.slice(1);
                return {
                    past: [...past, present],
                    present: {
                        ...next,
                        sources: {
                            ...next.sources,
                            tilejson: present.sources.tilejson,
                            metadata: present.sources.metadata,
                        },
                        chat: present.chat,
                        apiKey: present.apiKey,
                        init: present.init,
                    },
                    future: newFuture,
                    primaryVersion: null,
                    secondaryVersion: null,
                };
            }
            case restoreVersion.type: {
                const { payload: index } = action;
                if (index <= 0 || index > past.length) {
                    return state;
                }
                const target = past[index - 1];
                return {
                    past: [present, ...past],
                    present: {
                        ...target,
                        sources: {
                            ...target.sources,
                            tilejson: present.sources.tilejson,
                            metadata: present.sources.metadata,
                        },
                        chat: present.chat,
                        apiKey: present.apiKey,
                        init: {
                            ...target.init,
                            current: false,
                            published: false,
                        },
                    },
                    future: [],
                    primaryVersion: null,
                    secondaryVersion: null,
                };
            }
            case setPrimaryVersion.type: {
                const { payload } = action;
                return {
                    ...state,
                    primaryVersion: payload,
                };
            }
            case setSecondaryVersion.type: {
                const { payload } = action;
                return {
                    ...state,
                    secondaryVersion: payload,
                };
            }
            case changeChatId.type: {
                const { payload: { id, history }, } = action;
                if (savedIntState === undefined) {
                    return state;
                }
                const startingState = {
                    ...savedIntState.present,
                    chat: {
                        ...savedIntState.present.chat,
                        chatOpen: present.chat.chatOpen,
                        historyOpen: present.chat.historyOpen,
                        chatId: id,
                    },
                    apiKey: present.apiKey,
                    sources: {
                        ...savedIntState.present.sources,
                        tilejson: present.sources.tilejson,
                        metadata: present.sources.metadata,
                    },
                };
                if (history.length === 0) {
                    return {
                        ...state,
                        past: [],
                        present: startingState,
                        future: [],
                        primaryVersion: null,
                        secondaryVersion: null,
                    };
                }
                const lastEntry = history[history.length - 1];
                const pastEntries = history.slice(0, -1);
                return {
                    ...state,
                    past: [
                        ...pastEntries.toReversed().map(entry => entryToState(startingState, entry)),
                        startingState,
                    ],
                    present: entryToState(startingState, lastEntry),
                    future: [],
                    primaryVersion: null,
                    secondaryVersion: null,
                };
            }
            case assistantChange.type: {
                const { payload: { chatId, entry }, } = action;
                if (chatId !== present.chat.chatId) {
                    return state;
                }
                const newPresent = entryToState(present, entry);
                return {
                    past: [present, ...past],
                    present: newPresent,
                    future: [],
                    primaryVersion: null,
                    secondaryVersion: null,
                };
            }
            /**
             * This actions should not generate history
             * Only actions that change spec should be added to history
             */
            case changeSearch.type:
            case selectLayer.type:
            case selectSource.type:
            case selectSourceLayer.type:
            case selectSourceLayerLayer.type:
            case setMapkaApiKey.type:
            case addSourceTileJSON.type:
            case addSourceMetadata.type:
            case setLayersDisplay.type:
            case setLayersConfigDisplay.type:
            case addHighlightLayers.type:
            case removeHighlightLayers.type:
            case toggleHistory.type:
            case toggleChat.type:
            case selectMarker.type:
                return {
                    past,
                    present: reducer(present, action),
                    future,
                    primaryVersion: null,
                    secondaryVersion: null,
                };
            default: {
                const newPresent = reducer(present, action);
                if (present === newPresent) {
                    return state;
                }
                return {
                    past: [present, ...past],
                    present: {
                        ...newPresent,
                        init: {
                            action: action,
                            style: newPresent.init.style,
                            spec: newPresent.init.spec,
                            accountName: newPresent.init.accountName,
                            published: false,
                            current: false,
                        },
                    },
                    future: [],
                    primaryVersion: null,
                    secondaryVersion: null,
                };
            }
        }
    };
}
const selectState = (state) => state;
export const hasPastSelector = createSelector([selectState], state => state.past.length > 0);
export const hasFutureSelector = createSelector([selectState], state => state.future.length > 0);
